import { isTauri } from "@/lib/is-tauri";

/** Stable, token-free stamp for auth-sensitive desktop chat state. */
export interface DesktopChatAuthScope {
  accountId: string;
  epoch: string;
}

export const DESKTOP_CHAT_AUTH_SCOPE_EVENT =
  "denker://desktop-chat-auth-scope";
export const DESKTOP_CHAT_AUTH_INVALIDATED_EVENT =
  "denker://desktop-chat-auth-invalidated";

const STORAGE_KEY = "denker.desktop.chat_auth_scope.v1";
let currentScope: DesktopChatAuthScope | null = null;
const listeners = new Set<() => void>();

function isScope(value: unknown): value is DesktopChatAuthScope {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<DesktopChatAuthScope>;
  return (
    typeof candidate.accountId === "string" &&
    candidate.accountId.length > 0 &&
    typeof candidate.epoch === "string" &&
    candidate.epoch.length > 0
  );
}

function sameScope(
  left: DesktopChatAuthScope | null,
  right: DesktopChatAuthScope | null,
): boolean {
  return left?.accountId === right?.accountId && left?.epoch === right?.epoch;
}

function readStoredScope(): DesktopChatAuthScope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isScope(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredScope(scope: DesktopChatAuthScope | null): void {
  try {
    if (scope) localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Live webviews still converge through the Tauri scope event.
  }
}

export function getDesktopChatAuthScope(): DesktopChatAuthScope | null {
  return currentScope;
}

export function resolveDesktopChatAuthScope(
  accountId: string,
  createIfMissing: boolean,
): DesktopChatAuthScope | null {
  if (currentScope?.accountId === accountId) return currentScope;
  const stored = readStoredScope();
  if (stored?.accountId === accountId) return stored;
  if (!createIfMissing) return null;
  return { accountId, epoch: crypto.randomUUID() };
}

export function updateDesktopChatAuthScope(value: unknown): boolean {
  const nextScope = isScope(value) ? value : null;
  if (sameScope(nextScope, currentScope)) return false;
  currentScope = nextScope;
  writeStoredScope(nextScope);
  for (const listener of listeners) listener();
  return true;
}

export function isCurrentDesktopChatAuthScope(value: unknown): boolean {
  return isScope(value) && sameScope(value, currentScope);
}

export function isActiveDesktopChatAuthScope(value: unknown): boolean {
  return isScope(value) && sameScope(value, readStoredScope());
}

export function subscribeDesktopChatAuthScope(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function captureDesktopChatAuthScopeGuard(): () => boolean {
  const sourceScope = currentScope;
  if (!isTauri()) return () => sameScope(sourceScope, currentScope);
  return () => isCurrentDesktopChatAuthScope(sourceScope);
}
